/**
 * Utility functions for encoding/decoding modal state in URL query parameters
 * Used to restore modals after redirects (e.g., from Stripe payment)
 */

export type ModalType = 'allianceRequest' | 'publishJob' | 'quoteRequest' | 'quote';

export interface ModalParams {
  type: ModalType;
  freelancerId?: string;
  jobId?: string;
  quoteRequestId?: string;
  quoteId?: string;
}

/**
 * Encodes modal parameters into URL query string
 */
export function encodeModalParams(params: ModalParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('openModal', params.type);

  if (params.freelancerId) {
    searchParams.set('freelancerId', params.freelancerId);
  }
  if (params.jobId) {
    searchParams.set('jobId', params.jobId);
  }
  if (params.quoteRequestId) {
    searchParams.set('quoteRequestId', params.quoteRequestId);
  }
  if (params.quoteId) {
    searchParams.set('quoteId', params.quoteId);
  }

  return searchParams.toString();
}

/**
 * Decodes modal parameters from URL query string
 */
export function decodeModalParams(searchParams: URLSearchParams): ModalParams | null {
  const modalType = searchParams.get('openModal') as ModalType | null;
  if (!modalType) return null;

  const params: ModalParams = { type: modalType };

  const freelancerId = searchParams.get('freelancerId');
  if (freelancerId) params.freelancerId = freelancerId;

  const jobId = searchParams.get('jobId');
  if (jobId) params.jobId = jobId;

  const quoteRequestId = searchParams.get('quoteRequestId');
  if (quoteRequestId) params.quoteRequestId = quoteRequestId;

  const quoteId = searchParams.get('quoteId');
  if (quoteId) params.quoteId = quoteId;

  return params;
}

/**
 * Builds a return URL with modal parameters
 */
export function buildReturnUrlWithModal(basePath: string, modalParams: ModalParams | null): string {
  if (!modalParams) {
    return basePath;
  }

  const searchParams = encodeModalParams(modalParams);
  return `${basePath}?${searchParams}`;
}

/**
 * Removes all modal-related parameters from URL search params
 */
export function cleanupModalParamsFromUrl(
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams, options: { replace: boolean }) => void
): void {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete('openModal');
  newSearchParams.delete('freelancerId');
  newSearchParams.delete('jobId');
  newSearchParams.delete('quoteRequestId');
  newSearchParams.delete('quoteId');
  setSearchParams(newSearchParams, { replace: true });
}
